
import {of as observableOf,  Observable } from 'rxjs';

import {catchError} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { IAddNewContact, InvitationsPage, InvitationsPagedData, IUpdateBatchInvitation, IUpdateInvitation } from '@routes/events/models/invitationlist-model';
import { IAttendeeDetails, IContactsToAdd, IInvitation, IInvitationBatch, IInvitationContactDetails, IInvitationExportDetails, IInvitationPrintDetails,
    IInvitationRegistrationPage, IInvitations, IInvitationStatus, IInvitationsToEdit } from '@shared/services/api/legacy/invitations-service/invitations-model';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/legacy/response';
import { IAdvancedSortSettings } from '../../dashboard/invitations-service/invitations-model';

@Injectable()
export class InvitationsService {

    constructor(private http: HttpClient) { }

    private static get URL(): string { return `${environment.api.baseUrl}${environment.api.prefix}${InvitationsService.ENDPOINT}`; }
    private static readonly ENDPOINT = '/events/{eventId}/invitations';

    public getInvitations(eventId: string, page: InvitationsPage): Observable<SuccessResponse<InvitationsPagedData<IInvitation>> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<InvitationsPagedData<IInvitation>>>(InvitationsService.URL.replace('{eventId}', eventId), page).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getInvitationExport(eventId: string, page: InvitationsPage): Observable<SuccessResponse<IInvitationExportDetails> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<IInvitationExportDetails>>(InvitationsService.URL.replace('{eventId}', eventId) + '/exports', page).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getInvitationPrint(eventId: string, page: InvitationsPage): Observable<SuccessResponse<IInvitationPrintDetails> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<IInvitationPrintDetails>>(InvitationsService.URL.replace('{eventId}', eventId) + '/prints', page).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getInvitationAttendees(eventId: string, invitationId: string): Observable<SuccessResponse<IAttendeeDetails> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IAttendeeDetails>>(InvitationsService.URL.replace('{eventId}', eventId) + '/' + invitationId + '/attendees').pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getInvitationStatus(eventId: string, invitationId: string): Observable<SuccessResponse<IInvitationStatus> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IInvitationStatus>>(InvitationsService.URL.replace('{eventId}', eventId) + '/' + invitationId + '/statusses').pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getInvitationContact(eventId: string, invitationId: string): Observable<SuccessResponse<IInvitationContactDetails> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IInvitationContactDetails>>(InvitationsService.URL.replace('{eventId}', eventId) + '/' + invitationId + '/contacts').pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getInvitationRegistrationPage(eventId: string, invitationId: string): Observable<SuccessResponse<IInvitationRegistrationPage> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IInvitationRegistrationPage>>(InvitationsService.URL.replace('{eventId}', eventId) + '/' + invitationId + '/registrationpages').pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getDetailsInvitations(eventId: string): Observable<SuccessResponse<IInvitations> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IInvitations>>(InvitationsService.URL.replace('{eventId}', eventId) + '/details').pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateInvitations(eventId: string, model: IInvitationsToEdit): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(InvitationsService.URL.replace('{eventId}', eventId), model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public sendInvitation(eventId: string, invitationId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(InvitationsService.URL.replace('{eventId}', eventId) + '/' + invitationId + '/send', { }).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addUser(eventId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(InvitationsService.URL.replace('{eventId}', eventId) + '/users', null).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addContacts(eventId: string, model: IContactsToAdd): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(InvitationsService.URL.replace('{eventId}', eventId) + '/contacts', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addNewContact(eventId: string, model: IAddNewContact): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(InvitationsService.URL.replace('{eventId}', eventId) + '/contacts/create', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deleteInvitation(eventId: string, invitationId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .delete<SuccessResponse<boolean>>(InvitationsService.URL.replace('{eventId}', eventId) + '/' + invitationId).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateInvitation(eventId: string, model: IUpdateInvitation): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(InvitationsService.URL.replace('{eventId}', eventId) + '/' + model.id , model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public moveToTop(eventId: string, invitationId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(InvitationsService.URL.replace('{eventId}', eventId) + '/' + invitationId + '/rankings/top', { }).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public moveToBottom(eventId: string, invitationId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(InvitationsService.URL.replace('{eventId}', eventId) + '/' + invitationId + '/rankings/bottom', { }).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public moveUp(eventId: string, invitationId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(InvitationsService.URL.replace('{eventId}', eventId) + '/' + invitationId + '/rankings/up', { }).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public moveDown(eventId: string, invitationId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(InvitationsService.URL.replace('{eventId}', eventId) + '/' + invitationId + '/rankings/down', { }).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public shuffle(eventId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(InvitationsService.URL.replace('{eventId}', eventId) + '/rankings/shuffle', { }).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deleteInvitations(eventId: string, invitations: IInvitationBatch): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(InvitationsService.URL.replace('{eventId}', eventId) + '/deletebatch', invitations).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateInvitationsBatch(eventId: string, model: IUpdateBatchInvitation): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(InvitationsService.URL.replace('{eventId}', eventId) + '/updatebatch', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }
}
